/**
 * FairCorp — Public Quotation Calculator (V1 Public Estimator)
 *
 * Pure, read-only estimator logic. Runs entirely in the browser.
 * No persistence, no submission, no customer data collection.
 *
 * Pricing sources:
 * - DEVELOPMENT_TIERS: approved development price ranges / starting prices.
 * - HOSTING: approved base hosting plan prices ($/mo).
 * - SCORE_BANDS + RISK_ALLOWANCE: PROVISIONAL — pending approval/calibration.
 *   T4+ always routes to an engineering-based staff quote, never a formula.
 */

/**
 * Development tiers. T3 was split into T3A (presentation) and T3B (third-party
 * integration) by OWNER-AUTHORIZED POLICY CHANGE dated 2026-10-14. Both share the
 * identical approved $1,500–$5,000 range; the split is about where effort goes.
 * T3A/T3B are DEVELOPMENT classifications — not the H3a/H3b hosting configurations.
 */
export type Tier = 'T1' | 'T2' | 'T3A' | 'T3B' | 'T4A' | 'T4B' | 'T5';
export type Panel = 'none' | 'content' | 'advanced';
export type Risk = 0 | 1 | 2 | 3 | 4 | 5;
export type Rating = 0 | 1 | 2 | 3 | 4 | 5;

export const isTier3 = (t: Tier): t is 'T3A' | 'T3B' => t === 'T3A' || t === 'T3B';

export interface EstimateOptions {
  tier: Tier;
  baseDevelopmentPrice: number;
  panel: Panel;
  aiSelfHealing: boolean;
  fairCorpHosting: boolean;
  highTraffic: boolean;
  hostingRisk: Risk;
  /** T3A and T3B both qualify: one Tier 3 commission, scope-review if it cannot fit the range. */
  t3Both?: boolean;
  /** Visitor wants to administer customer orders / third-party account data from the panel. */
  unifiedAdminPanel?: boolean;
  /**
   * T3B with a small FairCorp-hosted API adapter (credentials, forwarding, mapping).
   * Hosting eligibility only — does not change the plan's dollar price.
   * Provider-hosted or frontend-only T3B stays H3a unless content management or high traffic applies.
   */
  lightweightAdapter?: boolean;
}

/** A–F project ratings. F is development liability; hosting risk is rated separately. */
export interface ProjectRatings {
  A: Rating; // Visual complexity
  B: Rating; // Content scope
  C: Rating; // Functionality
  D: Rating; // Integration & data
  E: Rating; // Delivery & infrastructure
  F: Rating; // Security liability/risk
}

export type CategoryCode = 'A' | 'B' | 'C' | 'D' | 'E' | 'F';
export type Scores = ProjectRatings;

export const HOSTING = {
  H1a: 15, H1b: 25,
  H2a: 25, H2b: 35,
  H3a: 35, H3b: 50,
  H4a1: 100, H4a2: 110, H4a3: 150,
  H4b1: 175, H4b2: 200,
  H4b3: 250, H4b4: 300,
  H5a: 400, H5b: 425,
  H5c: 500, H5d: 600,
} as const;

export type HostingPlan = keyof typeof HOSTING;

/**
 * Public availability of each approved hosting plan (owner decision 2026-10-23).
 * H5 base rates stay in HOSTING for future re-enablement, but H5 is not offered
 * at this time and must not be presented as a purchasable monthly price.
 */
export const HOSTING_AVAILABILITY: Record<HostingPlan, boolean> = {
  H1a: true, H1b: true,
  H2a: true, H2b: true,
  H3a: true, H3b: true,
  H4a1: true, H4a2: true, H4a3: true,
  H4b1: true, H4b2: true, H4b3: true, H4b4: true,
  H5a: false, H5b: false, H5c: false, H5d: false,
};

export function isHostingPlanAvailable(plan: HostingPlan): boolean {
  return HOSTING_AVAILABILITY[plan];
}

export const H5_HOSTING_NOTICE = 'H5 hosting (H5a–H5d) is not available at this time.';

/**
 * ILLUSTRATIVE PLACEHOLDER — NOT APPROVED POLICY (spec §10.5, §13.4).
 * The 0/0/5/15/30% values must not be presented as approved FairCorp pricing.
 * null = custom security + hosting assessment, no automatic total.
 */
export const RISK_ALLOWANCE = [0, 0, 0.05, 0.15, 0.3, null] as const;

export const AI_SELF_HEALING_PRICE = 2000;
export const CONTENT_PANEL_MULTIPLIER = 0.75;
/** Advanced panel floor is 1.00×; greater intensity is custom-quoted. */
export const ADVANCED_PANEL_MIN_MULTIPLIER = 1.0;

/** Approved development prices. max null = open-ended, staff-quoted. */
export const DEVELOPMENT_TIERS = {
  T1: { min: 200, max: 500 },
  T2: { min: 500, max: 1500 },
  // Owner-authorized 2026-10-14: T3A and T3B carry identical approved ranges.
  T3A: { min: 1500, max: 5000 },
  T3B: { min: 1500, max: 5000 },
  T4A: { min: 5000, max: null },
  T4B: { min: 8000, max: null },
  // Owner-authorized 2026-10-23: T5 starting price raised from $10,000 to $18,000.
  T5: { min: 18000, max: null },
} as const;

/**
 * ILLUSTRATIVE PLACEHOLDER — NOT APPROVED (spec §9.6, §13.4, §15.1).
 * Complexity-score bands for T1–T3 interpolation. Thresholds, weights, and
 * formulas are not finalized; F contributes to the score without changing
 * the approved $200–$5,000 ranges. Must stay configurable until approved.
 */
export const SCORE_BANDS: Record<'T1' | 'T2' | 'T3A' | 'T3B', { low: number; high: number }> = {
  T1: { low: 0, high: 6 },
  T2: { low: 5, high: 12 },
  // Both Tier 3 variants share the existing T3 band (no new formula invented).
  T3A: { low: 10, high: 18 },
  T3B: { low: 10, high: 18 },
};

export const TIERS: Tier[] = ['T1', 'T2', 'T3A', 'T3B', 'T4A', 'T4B', 'T5'];

export interface TierInfo {
  tier: Tier;
  name: string;
  tagline: string;
  description: string;
  priceDisplay: string;
  priceNote: string;
  hostingRange: string;
  features: string[];
  bestFor: string;
}

export const TIER_INFO: TierInfo[] = [
  {
    tier: 'T1',
    name: 'Tier 1 — Single-Page Informational Website',
    tagline: 'One custom page: landing, portfolio, or public info.',
    description:
      'Custom single-page informational website with standard responsive layout, section navigation, and custom styling and branding. No substantial application backend.',
    priceDisplay: '$200 – $500',
    priceNote: 'Approved range',
    hostingRange: 'H1a ($15/mo) – H1b ($25/mo)',
    features: ['One principal page', 'Business or personal info', 'Responsive + section navigation', 'Custom styling and branding'],
    bestFor: 'Landing pages, basic portfolios, promos, simple public info sites.',
  },
  {
    tier: 'T2',
    name: 'Tier 2 — Multi-Page Informational Website',
    tagline: 'Custom multi-page presence for small business.',
    description:
      'Informational website with multiple pages, custom navigation and page structure, service and product descriptions, and visual content. Ordinary frontend interactions only.',
    priceDisplay: '$500 – $1,500',
    priceNote: 'Approved range',
    hostingRange: 'H2a ($25/mo) – H2b ($35/mo)',
    features: ['Multiple informational pages', 'Custom navigation + branding', 'Service / product descriptions', 'Photos and visual content'],
    bestFor: 'Restaurants, service businesses, company sites, product showcases.',
  },
  {
    tier: 'T3A',
    name: 'Tier 3A — Advanced Graphics & Motion Website',
    tagline: 'We build the experience: standout visual design, motion, and interaction.',
    description:
      'Websites requiring significantly more advanced visual design, graphics, motion, and frontend interaction. Sets the functional boundary for standard content-management interfaces.',
    priceDisplay: '$1,500 – $5,000',
    priceNote: 'Approved range — same as Tier 3B',
    hostingRange: 'H3a ($35/mo) – H3b ($50/mo)',
    features: ['Advanced graphical presentation', 'Custom motion and animation', 'Interactive frontend elements', 'Complex responsive components'],
    bestFor: 'Brands and showcases where presentation is the product.',
  },
  {
    tier: 'T3B',
    name: 'Tier 3B — Third-Party Integrated Functionality Website',
    tagline: 'We connect existing services: ordering, booking, checkout, scheduling.',
    description:
      'Websites whose business functionality is delegated to existing external providers and needs substantial integration and workflow design. The provider stays responsible for its underlying service under its own contract; FairCorp is responsible for the integration work it commissions and its own parts of the site. A small FairCorp-hosted adapter that only talks to a third-party API the customer already uses — authentication, forwarding, mapping, retries — stays in this tier. Substantive processing of the customer’s own business data is Tier 4A. What is possible depends on the options the provider supports — some offer only links or hosted checkout.',
    priceDisplay: '$1,500 – $5,000',
    priceNote: 'Approved range — same as Tier 3A',
    hostingRange: 'H3a ($35/mo) – H3b ($50/mo)',
    features: [
      'Substantial integration of supported third-party services',
      'Workflow design, validation, and compatibility checks',
      'Provider remains responsible for its own service',
      'Third-party fees stay separate (to be confirmed)',
    ],
    bestFor: 'A restaurant connecting supported ordering flows, a salon on an existing scheduling platform, a retailer using an externally managed checkout.',
  },
  {
    tier: 'T4A',
    name: 'Tier 4A — Non-Database Backend Application',
    tagline: 'Substantive backend logic, no significant database.',
    description:
      'Custom APIs, server-side processing, backend business logic, integrations, and server-managed workflows — without a substantial application-database requirement. No upper price limit.',
    priceDisplay: 'From $5,000',
    priceNote: 'Approved starting price — engineering quote required',
    hostingRange: 'H4a1 ($100/mo) – H4a3 ($150/mo)',
    features: ['Custom APIs + backend logic', 'Server-side processing', 'App-specific integrations', 'Advanced panel eligible (1.00× min)'],
    bestFor: 'Tools, calculators, workflow services, integration-heavy apps.',
  },
  {
    tier: 'T4B',
    name: 'Tier 4B — Database-Backed Application',
    tagline: 'Persistent structured data, accounts, auth.',
    description:
      'Backend applications with database integration, persistent application records, user accounts, data retrieval and modification, and authentication. No upper price limit.',
    priceDisplay: 'From $8,000',
    priceNote: 'Approved starting price — engineering quote required',
    hostingRange: 'H4b1 ($175/mo) – H4b4 ($300/mo)',
    features: ['Database + persistent records', 'User accounts + auth', 'Database-backed operations', 'Advanced panel eligible (1.00× min)'],
    bestFor: 'Booking systems, client portals, record-keeping apps.',
  },
  {
    tier: 'T5',
    name: 'Tier 5 — Advanced Reliability, Sensitive Data & Infrastructure',
    tagline: 'Resilience, redundancy, and sensitive-data responsibility.',
    description:
      'T5 is for platforms where ordinary hosting is not enough: sensitive or regulated data, critical availability, very high traffic, or infrastructure spread across multiple systems. It is an engineering and security engagement first, not simply a larger website build. No upper price limit.',
    priceDisplay: 'From $18,000',
    priceNote: 'Approved starting price — custom quote required',
    hostingRange: 'H5 hosting is not available at this time',
    features: ['Complex / resilient architecture', 'Sensitive-data handling', 'Redundancy + recovery capability', 'High-reliability operations', 'Dedicated architecture and security review'],
    bestFor: 'Regulated, high-traffic, or multi-system platforms.',
  },
];

// ---------------------------------------------------------------------------
// A–F requirements assessment
// ---------------------------------------------------------------------------

export interface AssessmentOption {
  value: number;
  label: string;
}

export interface AssessmentQuestion {
  code: CategoryCode;
  title: string;
  hint: string;
  options: AssessmentOption[];
}

const scale = (labels: [string, string, string, string, string, string]): AssessmentOption[] =>
  labels.map((label, value) => ({ value, label }));

export const ASSESSMENT_QUESTIONS: AssessmentQuestion[] = [
  {
    code: 'A',
    title: 'How custom should the design look?',
    hint: 'Think about branding, layout, photography, and motion. Pick the closest match.',
    options: scale([
      'Simple and ready-made',
      'Lightly themed',
      'Custom styling and photo galleries',
      'Rich visuals, animation, and media',
      'A bespoke design system with motion',
      'A highly custom, experiential design',
    ]),
  },
  {
    code: 'B',
    title: 'How much content will you launch with?',
    hint: 'Count the main pages and the amount of photography or writing you expect.',
    options: scale([
      'One page',
      '2–5 simple pages',
      '6–15 pages with photos',
      '15+ pages or many rich sections',
      'A large library that needs search',
      'A very large, multi-section catalog',
    ]),
  },
  {
    code: 'C',
    title: 'What should visitors be able to do?',
    hint: 'Focus on features, not traffic. Pick the closest match.',
    options: scale([
      'Read information only',
      'Send a contact or lead form',
      'Filter, browse galleries, or view embeds',
      'Use custom workflows or calculators',
      'Use dashboards, roles, or approvals',
      'Complete complex, multi-step tasks',
    ]),
  },
  {
    code: 'D',
    title: 'Will the site store data or connect to other services?',
    hint: 'Include apps, databases, and outside services such as payments or booking.',
    options: scale([
      'No stored data',
      'Forms that email you only',
      'Simple lists or embedded tools',
      'App data with one or two integrations',
      'Persistent records and several integrations',
      'A multi-system data platform',
    ]),
  },
  {
    code: 'E',
    title: 'How demanding is the launch?',
    hint: 'Consider traffic, uptime, and deployment complexity — not how sensitive the data is.',
    options: scale([
      'Brochure traffic, simple deployment',
      'Small-business traffic',
      'Growth expected, caching or a CDN',
      'Sustained traffic, staging, and automated deployment',
      'High availability and a scaling plan',
      'Extreme scale, high availability, or compliance-grade operations',
    ]),
  },
  {
    code: 'F',
    title: 'How sensitive is the data and the risk?',
    hint: 'Consider what could go wrong and who is responsible. This is separate from technical difficulty.',
    options: scale([
      'Public information only',
      'Contact forms and limited personal data',
      'Business information, limited personal records, or an admin login',
      'Customer accounts, personal data, or key business records',
      'Confidential or financial data with contractual security duties',
      'Regulated data, large-scale exposure, or mission-critical operations',
    ]),
  },
];

export const HOSTING_RISK_OPTIONS: { value: Risk; label: string; detail: string }[] = [
  { value: 0, label: 'F0 — Minimal', detail: 'Proposed +0% · Standard review' },
  { value: 1, label: 'F1 — Low', detail: 'Proposed +0% · Standard review' },
  { value: 2, label: 'F2 — Moderate', detail: 'Proposed +5% · Standard review' },
  { value: 3, label: 'F3 — Elevated', detail: 'Proposed +15% · Security review' },
  { value: 4, label: 'F4 — High', detail: 'Proposed +30% · Custom security review' },
  { value: 5, label: 'F5 — Critical', detail: 'Custom quote · Mandatory manual assessment' },
];

export function riskRate(risk: Risk): number | null {
  return RISK_ALLOWANCE[risk];
}

// ---------------------------------------------------------------------------
// Scoring + classification (provisional thresholds; subject to approval)
// ---------------------------------------------------------------------------

export function getComplexityScore(r: ProjectRatings): number {
  return r.A + r.B + r.C + r.D + r.E + r.F;
}

/**
 * "How will your site's business functionality work?" — the capability question
 * added with the T3A/T3B split (owner-authorized 2026-10-14). It lives in the
 * existing step 2 and complements C (workflow) and D (integrations & data).
 */
export type BusinessFunction = 'informational' | 'thirdParty' | 'customBackend' | 'customBackendDb';
export type IntegrationDepth = 'linkOrEmbed' | 'substantial';

/**
 * Where backend work sits. Owner policy 2026-10-14 (T3B / T4A boundary):
 * a lightweight adapter that talks to a third-party API is T3B; substantive
 * original processing is T4A; persisting application data is T4B.
 */
export type BackendResponsibility =
  | 'none'
  | 'third_party_delegated'
  | 'lightweight_api_adapter'
  | 'custom_processing'
  | 'persistent_database';

export interface ProjectRequirements {
  businessFunction: BusinessFunction;
  /** Only read when businessFunction === 'thirdParty' and the visitor is not on the adapter path. */
  integrationDepth: IntegrationDepth;
  /**
   * Third-party credentials, webhooks, or a small server-side adapter.
   * Owner policy 2026-10-14: this is T3B (lightweight adapter), not T4A.
   */
  needsServerProcessing: boolean;
  /** thirdParty + stored orders / customer accounts / persistent records → FairCorp holds data (T4B). */
  needsStoredRecords: boolean;
  backendResponsibility: BackendResponsibility;
}

/** Default = the pre-split behavior: score-driven classification only. */
export const DEFAULT_REQUIREMENTS: ProjectRequirements = {
  businessFunction: 'informational',
  integrationDepth: 'linkOrEmbed',
  needsServerProcessing: false,
  needsStoredRecords: false,
  backendResponsibility: 'none',
};

/** Customer-facing handling question. `informational` stays the untouched default so score-only quotes do not change. */
export type SiteHandling = 'informational' | 'links' | 'adapter' | 'processing' | 'database';

export const SITE_HANDLING_OPTIONS: { value: SiteHandling; label: string; detail: string }[] = [
  {
    value: 'informational',
    label: 'Informational only',
    detail: 'No significant ordering, booking, or business workflow.',
  },
  {
    value: 'links',
    label: 'Use existing external services through links or embedded tools',
    detail: 'A button or an embed. A simple link stays T1–T2. Choose substantial integration below when the provider still handles orders.',
  },
  {
    value: 'adapter',
    label: 'Connect to third-party services through a small custom integration service',
    detail: 'FairCorp hosts a small service that authenticates, forwards, and maps a third-party API you already use. Priced inside the T3B range.',
  },
  {
    value: 'processing',
    label: 'Build custom services to ingest, clean, parse, and process business data',
    detail: 'FairCorp builds original processing — cleaning, validation, aggregation, or business-specific calculations. Tier 4A, engineering quote.',
  },
  {
    value: 'database',
    label: 'Build custom processing with persistent application database storage',
    detail: 'Custom processing plus normalized records or an application database FairCorp persists. Tier 4B.',
  },
];

export function isLightweightAdapter(req: ProjectRequirements): boolean {
  if (req.backendResponsibility === 'custom_processing' || req.backendResponsibility === 'persistent_database') return false;
  if (req.businessFunction === 'customBackend' || req.businessFunction === 'customBackendDb') return false;
  if (req.needsStoredRecords) return false;
  return req.backendResponsibility === 'lightweight_api_adapter' || (req.businessFunction === 'thirdParty' && req.needsServerProcessing);
}

export function requirementsForHandling(handling: SiteHandling, prev: ProjectRequirements = DEFAULT_REQUIREMENTS): ProjectRequirements {
  switch (handling) {
    case 'informational':
      return { ...DEFAULT_REQUIREMENTS };
    case 'links':
      return {
        businessFunction: 'thirdParty',
        integrationDepth: prev.businessFunction === 'thirdParty' && !prev.needsServerProcessing ? prev.integrationDepth : 'linkOrEmbed',
        needsServerProcessing: false,
        needsStoredRecords: false,
        backendResponsibility: 'third_party_delegated',
      };
    case 'adapter':
      return {
        businessFunction: 'thirdParty',
        integrationDepth: 'substantial',
        needsServerProcessing: true,
        needsStoredRecords: false,
        backendResponsibility: 'lightweight_api_adapter',
      };
    case 'processing':
      return {
        businessFunction: 'customBackend',
        integrationDepth: 'linkOrEmbed',
        needsServerProcessing: false,
        needsStoredRecords: false,
        backendResponsibility: 'custom_processing',
      };
    case 'database':
      return {
        businessFunction: 'customBackendDb',
        integrationDepth: 'linkOrEmbed',
        needsServerProcessing: false,
        needsStoredRecords: true,
        backendResponsibility: 'persistent_database',
      };
  }
}

export const BUSINESS_FUNCTION_OPTIONS: { value: BusinessFunction; label: string; detail: string }[] = [
  { value: 'informational', label: 'Informational only', detail: 'No significant ordering, booking, or business workflow.' },
  { value: 'thirdParty', label: 'Existing third-party service', detail: 'External providers handle ordering, payments, scheduling, or similar.' },
  { value: 'customBackend', label: 'Custom backend functionality', detail: 'FairCorp builds and operates new business logic or processing.' },
  { value: 'customBackendDb', label: 'Custom backend with database', detail: 'FairCorp builds persistent application records and related operations.' },
];

export const INTEGRATION_DEPTH_OPTIONS: { value: IntegrationDepth; label: string; detail: string }[] = [
  { value: 'linkOrEmbed', label: 'Simple link or embed', detail: 'A button to the provider, or a provider widget embedded as-is. Stays T1–T2.' },
  { value: 'substantial', label: 'Substantial integration', detail: 'A custom experience coordinating supported provider services. Tier 3B.' },
];

export interface Classification {
  tier: Tier;
  reasons: string[];
  /** Tier 3 only: which capabilities apply. Both = recorded together, ONE commission. */
  tier3?: { presentation: boolean; integration: boolean };
  /** Guidance / incompatible-answer messages (never part of `reasons`). */
  notes?: string[];
  /** True when answers contradict each other and were routed to a higher tier or review. */
  incompatible?: boolean;
}

/**
 * ILLUSTRATIVE PLACEHOLDER classifier — thresholds NOT FINALIZED (spec §9.5, §13.4, §15.1).
 * Functional triggers are independent so unrelated categories cannot mask
 * important requirements (e.g. low visual complexity hiding database needs,
 * or high graphics implying a backend app). Security exposure always triggers
 * review at F3+ regardless of tier.
 */
export function classifyProject(r: ProjectRatings, req: ProjectRequirements = DEFAULT_REQUIREMENTS): Classification {
  const reasons: string[] = [];
  const notes: string[] = [];
  const thirdParty = req.businessFunction === 'thirdParty';
  const explicitDb = req.businessFunction === 'customBackendDb' || req.backendResponsibility === 'persistent_database';
  const explicitProcessing = req.businessFunction === 'customBackend' || req.backendResponsibility === 'custom_processing';
  // Lightweight adapter (credentials, forwarding, mapping) is T3B as of 2026-10-14 — not T4A.
  const adapter = isLightweightAdapter(req);
  // Provider holds core processing. Score gates that invent a proprietary backend do not apply.
  // A–F floors themselves are unchanged: T5 is still E5/F5, T4B score gate D>=4, T4A score gate C>=4 or D>=3,
  // T3A is A>=3 or (C>=3 when the provider is not holding the work), T2 is B>=2.
  const providerHeld = (thirdParty || adapter) && !explicitDb && !explicitProcessing;

  // Priority 1 — advanced infrastructure / reliability / critical security obligations → T5.
  // (Unchanged from baseline: E5 or F5. Ordinary deployment complexity, E<5, does not qualify.)
  if (r.E === 5 || r.F === 5) {
    if (r.E === 5) reasons.push('E5/5 — delivery/infrastructure at extreme scale forces T5 scoping.');
    if (r.F === 5) reasons.push('F5/5 — critical exposure: T5 or individually negotiated enterprise commission, with explicit security, legal, and operational acceptance before quoting.');
    return { tier: 'T5', reasons };
  }

  // Priority 2 — substantive persistent application-database requirement → T4B.
  // Answer-driven, or the baseline D>=4 score trigger. The score trigger is NOT applied when the
  // visitor says an external provider holds the data: high D must not imply a proprietary backend.
  const dbByAnswer = explicitDb || (thirdParty && req.needsStoredRecords);
  const dbByScore = !providerHeld && r.D >= 4;
  if (dbByAnswer || dbByScore) {
    if (explicitDb) reasons.push('Capability — FairCorp develops persistent application records and related operations (database-backed build).');
    if (thirdParty && req.needsStoredRecords) {
      reasons.push('Stored orders, accounts, or records — FairCorp would hold persistent data, so a third-party-only build does not apply.');
      notes.push('Incompatible answers: "Existing third-party service" combined with stored orders/accounts/records. Routed to T4B.');
    }
    if (dbByScore) reasons.push(`D${r.D}/5 — persistent records and integrations need a database-backed build.`);
    if (dbByScore && !explicitDb) {
      notes.push(
        req.businessFunction === 'informational'
          ? 'D4+ describes stored records while "Informational only" is selected. If an external provider holds all of that data, choose "Existing third-party service" in step 2.'
          : 'D4+ describes stored records beyond "Custom backend functionality" without a database. Routed to T4B.',
      );
    }
    return { tier: 'T4B', reasons, notes: notes.length ? notes : undefined, incompatible: notes.length > 0 || undefined };
  }

  // Priority 3 — substantive original processing → T4A.
  // A lightweight adapter is deliberately NOT this branch (owner 2026-10-14).
  const serverByAnswer = explicitProcessing;
  const serverByScore = !providerHeld && (r.C >= 4 || r.D >= 3);
  if (serverByAnswer || serverByScore) {
    if (explicitProcessing) reasons.push('Capability — FairCorp develops and operates new business logic or server-side processing.');
    if (serverByScore) {
      if (r.C >= 4) reasons.push(`C${r.C}/5 — custom interactive functionality beyond a content site.`);
      if (r.D >= 3 && r.C < 4) reasons.push(`D${r.D}/5 — app data with integrations, no critical record store.`);
      if (req.businessFunction === 'informational') {
        notes.push('Your C/D answers describe application behavior or data while "Informational only" is selected. If external providers handle all of it, choose "Existing third-party service" in step 2.');
      }
    }
    return { tier: 'T4A', reasons, notes: notes.length ? notes : undefined, incompatible: notes.length > 0 || undefined };
  }

  // Priority 4/5 — Tier 3. T3B: substantial provider-delegated integration OR a lightweight API adapter.
  // T3A: advanced graphical/frontend presentation (A>=3, or the baseline C>=3 "custom workflows"
  // trigger — which is a provider workflow, not presentation, when external services are selected).
  const substantial = thirdParty && req.integrationDepth === 'substantial' && !adapter;
  const integration = adapter || substantial;
  const presentation = r.A >= 3 || (r.C >= 3 && !providerHeld);
  if (integration || presentation) {
    if (adapter) {
      reasons.push('Capability — small custom integration service (T3B — Integrated Services). Existing services remain responsible for core business processing.');
      notes.push('T3B — Integrated Services. Existing services remain responsible for core business processing. A lightweight adapter may authenticate, forward requests, map responses, and apply simple caching, retries, and rate limits. It does not ingest, clean, or aggregate the customer’s business data.');
    } else if (integration) {
      reasons.push(`Capability — substantial integration of existing external services (provider-managed workflow; C${r.C}/5, D${r.D}/5).`);
    }
    if (r.A >= 3) reasons.push(`A${r.A}/5 — visual complexity needs bespoke design and build.`);
    if (r.C >= 3 && !thirdParty) reasons.push(`C${r.C}/5 — custom workflows beyond a brochure site.`);
    const both = integration && presentation;
    if (both) {
      notes.push('Both Tier 3A (presentation) and Tier 3B (integration) apply. Both are recorded, but this is ONE Tier 3 commission — the two are never stacked.');
    }
    return {
      tier: integration ? 'T3B' : 'T3A',
      reasons,
      tier3: { presentation, integration },
      notes: notes.length ? notes : undefined,
    };
  }

  if (thirdParty) {
    notes.push('A simple link or embed to an external provider is ordinary navigation — it does not create Tier 3B.');
  }
  if (r.B >= 2) {
    reasons.push(`B${r.B}/5 — content volume beyond a simple static site.`);
    return { tier: 'T2', reasons, notes: notes.length ? notes : undefined };
  }
  return {
    tier: 'T1',
    reasons: ['All scores low — static pages with no accounts, database, or app scope.'],
    notes: notes.length ? notes : undefined,
  };
}

/**
 * Scope-review outcome for a project that qualifies as BOTH T3A and T3B. One Tier 3
 * commission is priced inside the shared range; if the combined scope sits at the
 * top of that range it may not be deliverable economically within it. The outcome is a
 * manual scope review — never a reclassification to T4. ILLUSTRATIVE trigger: uses the
 * provisional score band, no new formula.
 */
export function t3ScopeReview(tier: Tier, baseDevelopmentPrice: number, t3Both: boolean): boolean {
  if (!t3Both || !isTier3(tier)) return false;
  return baseDevelopmentPrice >= (DEVELOPMENT_TIERS[tier].max as number);
}

/**
 * F-rating review flags.
 * F3 = elevated security review; safeguards + security testing scoped into the estimate.
 * F4 = mandatory T5 assessment (does not force T5).
 * F5 = enterprise acceptance gate; a high F never auto-implies acceptance —
 * FairCorp may require specialist controls, insurance, external expertise, or decline.
 */
export function fReviewFlags(f: Rating): { securityReview: boolean; t5EligibilityAssessment: boolean } {
  return { securityReview: f >= 3, t5EligibilityAssessment: f >= 4 };
}

/**
 * Published payment schedule. T4B and T5 are quoted officially; the schedule is not a price.
 */
export function paymentSchedule(tier: Tier): string {
  if (tier === 'T1' || tier === 'T2') return '50% upfront, 50% at completion.';
  if (tier === 'T3A' || tier === 'T3B' || tier === 'T4A') return '40% upfront, 40% at three-fifths development completion, and 20% upon completion.';
  return 'FairCorp retains the right to specify payment terms during official quoting.';
}

// ---------------------------------------------------------------------------
// Pricing engine
// ---------------------------------------------------------------------------

/**
 * T1–T3: interpolate the approved range against the provisional tier band.
 * Rounded to the nearest $25. Returns null outside T1–T3 (staff quote).
 */
export function estimatePrice(tier: Tier, score: number): number | null {
  if (tier !== 'T1' && tier !== 'T2' && !isTier3(tier)) return null;
  const { min, max } = DEVELOPMENT_TIERS[tier];
  const { low, high } = SCORE_BANDS[tier];
  const normalized = Math.max(0, Math.min(1, (score - low) / (high - low)));
  return Math.round((min + normalized * ((max as number) - min)) / 25) * 25;
}

export interface DevelopmentBasis {
  /** Preliminary base development price shown in the estimator. */
  base: number;
  /** 'interpolated' for T1–T3, 'starting' for T4+ (engineering quote required). */
  kind: 'interpolated' | 'starting';
  note: string;
}

export function developmentBasis(tier: Tier, complexity: number): DevelopmentBasis {
  if (tier === 'T1' || tier === 'T2' || isTier3(tier)) {
    const base = estimatePrice(tier, complexity) ?? DEVELOPMENT_TIERS[tier].min;
    const band = SCORE_BANDS[tier];
    return {
      base,
      kind: 'interpolated',
      note: `Interpolated ${formatUSD(base)} within the approved ${tier} range (${formatUSD(DEVELOPMENT_TIERS[tier].min)}–${formatUSD(DEVELOPMENT_TIERS[tier].max as number)}), provisional score band ${band.low}–${band.high}.`,
    };
  }
  return {
    base: DEVELOPMENT_TIERS[tier].min,
    kind: 'starting',
    note: `Approved ${tier} starting price — final figure requires an engineering-based staff quote, not a formula. F exposure shapes security-engineering effort, testing, and contingency at review; it carries no fixed dollar surcharge.`,
  };
}

export function priceAddons(
  basePrice: number,
  panel: Panel,
  advancedMultiplier: number,
  aiSelfHealing: boolean,
) {
  const management =
    panel === 'content'
      ? basePrice * CONTENT_PANEL_MULTIPLIER
      : panel === 'advanced'
        ? basePrice * Math.max(ADVANCED_PANEL_MIN_MULTIPLIER, advancedMultiplier)
        : 0;
  const ai = aiSelfHealing ? AI_SELF_HEALING_PRICE : 0;
  return { management, ai, total: basePrice + management + ai };
}

// ---------------------------------------------------------------------------
// Eligibility + hosting selection
// ---------------------------------------------------------------------------

export function eligibleForAI(tier: Tier): boolean {
  return ['T4A', 'T4B', 'T5'].includes(tier);
}

export function eligibleForAdvancedPanel(tier: Tier): boolean {
  return eligibleForAI(tier);
}

export function selectHosting(o: EstimateOptions): keyof typeof HOSTING {
  const content = o.panel === 'content';
  const advanced = o.panel === 'advanced';

  switch (o.tier) {
    case 'T1':
      return content || o.highTraffic ? 'H1b' : 'H1a';
    case 'T2':
      return content || o.highTraffic ? 'H2b' : 'H2a';
    // Matching letters (T3B / H3b) imply nothing. H3b is content management, high traffic,
    // or a lightweight FairCorp-hosted API adapter. Prices are unchanged (H3a $35, H3b $50).
    case 'T3A':
      return content || o.highTraffic ? 'H3b' : 'H3a';
    case 'T3B':
      return content || o.highTraffic || o.lightweightAdapter ? 'H3b' : 'H3a';
    case 'T4A':
      if (o.aiSelfHealing || o.highTraffic || advanced) return 'H4a3';
      return content ? 'H4a2' : 'H4a1';
    case 'T4B':
      if (o.aiSelfHealing) return 'H4b4';
      if (advanced || o.highTraffic) return 'H4b3';
      return content ? 'H4b2' : 'H4b1';
    case 'T5':
      if (o.aiSelfHealing) return 'H5d';
      if (advanced || o.highTraffic) return 'H5c';
      return content ? 'H5b' : 'H5a';
  }
}

export interface EstimateResult {
  development: number;
  panelCost: number;
  aiCost: number;
  upfrontEstimate: number;
  hostingPlan: HostingPlan | null;
  baseMonthly: number;
  monthlyRiskAllowance: number | null;
  monthlyEstimate: number | null;
  requiresReview: boolean;
}

export function calculateEstimate(o: EstimateOptions): EstimateResult {
  if (o.panel === 'advanced' && !eligibleForAdvancedPanel(o.tier)) {
    throw new Error('Advanced panels require T4+');
  }
  if (o.aiSelfHealing && (!eligibleForAI(o.tier) || !o.fairCorpHosting)) {
    throw new Error('AI requires T4+ FairCorp hosting');
  }

  // The 0.75x standard panel covers FairCorp-site content only. A request to administer customer
  // orders / third-party account data from the panel needs an integration/backend assessment, so the
  // standard price is NOT applied automatically (the panel line is left to that assessment).
  const panelNeedsAssessment = !!o.unifiedAdminPanel && o.panel !== 'none';
  const pricedPanel: Panel = o.unifiedAdminPanel && o.panel === 'content' ? 'none' : o.panel;

  const { management: panelCost, ai: aiCost, total: upfrontEstimate } = priceAddons(
    o.baseDevelopmentPrice,
    pricedPanel,
    ADVANCED_PANEL_MIN_MULTIPLIER,
    o.aiSelfHealing,
  );

  const hostingPlan = o.fairCorpHosting ? selectHosting(o) : null;
  const baseMonthly = hostingPlan ? HOSTING[hostingPlan] : 0;
  const rate = RISK_ALLOWANCE[o.hostingRisk];

  const requiresReview =
    o.hostingRisk >= 4 ||
    (o.tier === 'T4A' && o.panel === 'advanced') ||
    panelNeedsAssessment ||
    t3ScopeReview(o.tier, o.baseDevelopmentPrice, !!o.t3Both);

  const monthlyEstimate =
    !o.fairCorpHosting ? 0 : rate === null ? null : Math.round(baseMonthly * (1 + rate) * 100) / 100;

  return {
    development: o.baseDevelopmentPrice,
    panelCost,
    aiCost,
    upfrontEstimate,
    hostingPlan,
    baseMonthly,
    monthlyRiskAllowance: monthlyEstimate === null ? null : monthlyEstimate - baseMonthly,
    monthlyEstimate,
    requiresReview,
  };
}

export function formatUSD(n: number): string {
  return n.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: n % 1 === 0 ? 0 : 2,
  });
}
